import React, { useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { RegistryItem } from '../types/registry';

interface Props {
  item: Partial<RegistryItem> & {
    name: string;
    price: number;
    priority: 'high' | 'medium' | 'low';
    allows_group_gifting: boolean;
  };
  currency: string;
  onClose: () => void;
  onUpdate: (item: Omit<RegistryItem, 'id' | 'registry_id' | 'is_claimed' | 'claimed_by' | 'created_at' | 'collected_amount'>) => void;
}

export default function EditItemModal({ item, currency, onClose, onUpdate }: Props) {
  const [name, setName] = useState(item.name);
  const [price, setPrice] = useState(item.price.toString());
  const [url, setUrl] = useState(item.url || '');
  const [priority, setPriority] = useState(item.priority);
  const [allowsGroupGifting, setAllowsGroupGifting] = useState(item.allows_group_gifting);
  const [targetAmount, setTargetAmount] = useState(item.target_amount?.toString() || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      const updateData = {
        name,
        price: Number(price),
        url: url || null,
        priority,
        allows_group_gifting: allowsGroupGifting,
        target_amount: allowsGroupGifting ? Number(targetAmount) : null
      };

      if (!item.id) {
        // This is a new item, let the parent handle the creation
        onUpdate(updateData);
      } else {
        // This is an existing item, update it directly
        const { error: updateError } = await supabase
          .from('registry_items')
          .update(updateData)
          .eq('id', item.id);

        if (updateError) throw updateError;
        onUpdate(updateData);
      }

      onClose();
    } catch (err) {
      console.error('Error updating item:', err);
      setError('Failed to update item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">
            {item.id ? 'Edit Gift Item' : 'Add Gift Item'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-900/50 text-red-200 p-3 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Item Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Price ({currency})
            </label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
              required
              min="0"
              step="0.01"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Product URL
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
              placeholder="https://"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Priority
            </label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as 'high' | 'medium' | 'low')}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="allowsGroupGifting"
                checked={allowsGroupGifting}
                onChange={(e) => setAllowsGroupGifting(e.target.checked)}
                className="h-4 w-4 bg-gray-900 border-gray-700 rounded text-primary focus:ring-primary"
              />
              <label htmlFor="allowsGroupGifting" className="ml-2 text-sm text-gray-300">
                Allow Group Gifting
              </label>
            </div>

            {allowsGroupGifting && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Target Amount ({currency})
                </label>
                <input
                  type="number"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  required
                  min={price}
                  step="0.01"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
            >
              {loading ? 'Saving...' : item.id ? 'Save Changes' : 'Add Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}